import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '../hooks/useAuth0';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  Flight,
  Hotel,
  DirectionsCar,
  CalendarToday,
  LocationOn,
  Phone,
  Person,
  CheckCircle,
  Pending,
  Cancel,
  Payment,
  Star,
  Info,
  Add,
  Search
} from '@mui/icons-material';
import api from '../services/api';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const [booking, setBooking] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `/travel/bookings/${id}` } });
    } else {
      fetchBookingDetails();
    }
  }, [id, isAuthenticated]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/travel/bookings/${id}`);
      setBooking(response.data);

      if (response.data.serviceTier) {
        try {
          const tierResponse = await api.get(`/v1/travel/tiers/${response.data.serviceTier}`);
          setTierInfo(tierResponse.data);
        } catch (tierErr) {
          console.warn('Could not load tier info:', tierErr);
        }
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching booking details:', err);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED': return <CheckCircle />;
      case 'PENDING': return <Pending />;
      case 'CANCELLED': return <Cancel />;
      default: return null;
    }
  };

  const getTierColor = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL': return '#1A5EA8';
      case 'CONCIERGE': return '#10b981';
      case 'WHITE_GLOVE': return '#f59e0b';
      default: return '#1A5EA8';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // Build travel search URLs using booking data
  const getFlightSearchUrl = () => {
  if (!booking) return '/travel/search?tab=flights';
  const params = new URLSearchParams();
  params.set('bookingId', booking.id);
  params.set('tab', 'flights');
  params.set('destination', booking.destinationCity);
  params.set('departureDate', booking.tripStartDate);
  params.set('returnDate', booking.tripEndDate);
  params.set('trialName', booking.trialName || '');  
  params.set('trialId', booking.trialId || ''); 
  return `/travel/search?${params.toString()}`;
};

  const getHotelSearchUrl = () => {
  if (!booking) return '/travel/search?tab=hotels';
  const params = new URLSearchParams();
  params.set('bookingId', booking.id);
  params.set('tab', 'hotels');
  params.set('city', booking.destinationCity);
  params.set('checkIn', booking.tripStartDate);
  params.set('checkOut', booking.tripEndDate);
  return `/travel/search?${params.toString()}`;
};

  const getCarSearchUrl = () => {
  if (!booking) return '/travel/search?tab=cars';
  const params = new URLSearchParams();
  params.set('bookingId', booking.id);
  params.set('tab', 'cars');
  params.set('city', booking.destinationCity);
  params.set('pickupDate', booking.tripStartDate);
  params.set('returnDate', booking.tripEndDate);
  return `/travel/search?${params.toString()}`;
};
  // Check if all components are booked — enables payment
  const isReadyForPayment = booking &&
    booking.flightBookingId &&
    booking.hotelBookingId &&
    booking.paymentStatus === 'PENDING';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error || 'Booking not found'}</Alert>
        <Button onClick={() => navigate('/travel/bookings')} sx={{ mt: 2 }}>
          Back to Bookings
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f9fafb', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/travel/bookings')} sx={{ mb: 3 }}>
          Back to Bookings
        </Button>

        {/* Header */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Booking Details
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Reference: <strong>{booking.bookingReference}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={getStatusIcon(booking.bookingStatus)}
                label={booking.bookingStatus}
                color={getStatusColor(booking.bookingStatus)}
              />
              <Chip
                label={booking.serviceTier?.replace('_', ' ')}
                sx={{ bgcolor: getTierColor(booking.serviceTier), color: 'white', fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>

            {/* Trial Information */}
            {booking.trialName && (
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Info sx={{ mr: 1, color: '#1A5EA8' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Clinical Trial</Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>{booking.trialName}</Typography>
                {booking.trialId && (
                  <Typography variant="caption" color="text.secondary">ID: {booking.trialId}</Typography>
                )}
              </Paper>
            )}

            {/* Destination */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: '#1A5EA8' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Destination</Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{booking.trialSiteName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {booking.trialSiteAddress ? `${booking.trialSiteAddress}, ` : ''}
                {booking.destinationCity}, {booking.destinationState}
              </Typography>
            </Paper>

            {/* Trip Dates */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ mr: 1, color: '#1A5EA8' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Trip Dates</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Departure</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(booking.tripStartDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Return</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(booking.tripEndDate)}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Contact Information */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1, color: '#1A5EA8' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Contact Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Your Phone</Typography>
                  <Typography variant="body1">{booking.patientPhone || 'Not provided'}</Typography>
                </Grid>
                {booking.patientEmergencyContact && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Emergency Contact</Typography>
                    <Typography variant="body1">
                      {booking.patientEmergencyContact}
                      {booking.patientEmergencyPhone && ` - ${booking.patientEmergencyPhone}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Special Requirements */}
            {booking.specialRequirements && (
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Special Requirements</Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9fafb' }}>
                  <Typography variant="body2">{booking.specialRequirements}</Typography>
                </Paper>
              </Paper>
            )}
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>

            {/* Service Level */}
            {tierInfo && (
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ mr: 1, color: getTierColor(booking.serviceTier) }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Service Level</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: getTierColor(booking.serviceTier), mb: 1 }}>
                  {tierInfo.tierName}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {tierInfo.tierDescription}
                </Typography>
                <List dense>
                  {tierInfo.featuresIncluded?.slice(0, 4).map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircle sx={{ fontSize: 18, color: getTierColor(booking.serviceTier) }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Travel Components */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Travel Components</Typography>

              {/* Step indicator */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Book all components before making payment
              </Typography>

              {/* Flight */}
              <Card sx={{ mb: 2, bgcolor: booking.flightBookingId ? '#f0fdf4' : '#f9fafb' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Flight sx={{ mr: 1, color: booking.flightBookingId ? '#10b981' : '#9ca3af' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Flight</Typography>
                    </Box>
                    <Chip
                      label={booking.flightBookingId ? 'Booked' : 'Not Booked'}
                      size="small"
                      color={booking.flightBookingId ? 'success' : 'default'}
                    />
                  </Box>
                  {booking.flightBookingId ? (
                    <Typography variant="caption" color="text.secondary">
                      {booking.flightAirline} · {booking.flightDepartureAirport} → {booking.flightArrivalAirport}
                    </Typography>
                  ) : (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<Search />}
                      onClick={() => navigate(getFlightSearchUrl())}
                      sx={{ mt: 1 }}
                    >
                      Search Flights
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Hotel */}
              <Card sx={{ mb: 2, bgcolor: booking.hotelBookingId ? '#f0fdf4' : '#f9fafb' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Hotel sx={{ mr: 1, color: booking.hotelBookingId ? '#10b981' : '#9ca3af' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Hotel</Typography>
                    </Box>
                    <Chip
                      label={booking.hotelBookingId ? 'Booked' : 'Not Booked'}
                      size="small"
                      color={booking.hotelBookingId ? 'success' : 'default'}
                    />
                  </Box>
                  {booking.hotelBookingId ? (
                    <Typography variant="caption" color="text.secondary">
                      {booking.hotelName} · {booking.hotelAddress}
                    </Typography>
                  ) : (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<Search />}
                      onClick={() => navigate(getHotelSearchUrl())}
                      sx={{ mt: 1 }}
                    >
                      Search Hotels
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Car Rental */}
              <Card sx={{ mb: 2, bgcolor: booking.groundTransportType ? '#f0fdf4' : '#f9fafb' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DirectionsCar sx={{ mr: 1, color: booking.groundTransportType ? '#10b981' : '#9ca3af' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Car Rental</Typography>
                    </Box>
                    <Chip
                      label={booking.groundTransportType ? 'Booked' : 'Optional'}
                      size="small"
                      color={booking.groundTransportType ? 'success' : 'default'}
                      variant={booking.groundTransportType ? 'filled' : 'outlined'}
                    />
                  </Box>
                  {booking.groundTransportType ? (
                    <Typography variant="caption" color="text.secondary">
                      {booking.groundTransportType}
                    </Typography>
                  ) : (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<Search />}
                      onClick={() => navigate(getCarSearchUrl())}
                      sx={{ mt: 1 }}
                    >
                      Search Cars
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Coordinator */}
              {booking.coordinatorName && (
                <Card sx={{ bgcolor: '#f0fdf4' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ mr: 1, color: '#10b981' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Travel Coordinator</Typography>
                    </Box>
                    <Typography variant="body2">{booking.coordinatorName}</Typography>
                  </CardContent>
                </Card>
              )}
            </Paper>

            {/* Payment Summary */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Payment sx={{ mr: 1, color: '#1A5EA8' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Payment Summary</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Service Fee</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${booking.serviceTierPrice?.toFixed(2)}
                  </Typography>
                </Box>
                {booking.flightAmount && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Flight</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${booking.flightAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {booking.hotelAmount && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Hotel</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${booking.hotelAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {booking.groundTransportAmount && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Car Rental</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${booking.groundTransportAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: getTierColor(booking.serviceTier) }}>
                  ${booking.totalAmount?.toFixed(2)}
                </Typography>
              </Box>

              <Chip
                label={`Payment: ${booking.paymentStatus}`}
                color={booking.paymentStatus === 'CAPTURED' ? 'success' : 'warning'}
                sx={{ width: '100%', mb: 2 }}
              />

              {booking.paymentStatus === 'PENDING' && (
                <>
                  {!isReadyForPayment && (
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
                      Please book your flight and hotel before making payment
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!isReadyForPayment}
                    onClick={() => navigate(`/travel/payment/${booking.id}`)}
                    sx={{
                      bgcolor: getTierColor(booking.serviceTier),
                      '&:hover': { bgcolor: getTierColor(booking.serviceTier) },
                      '&:disabled': { bgcolor: '#e5e7eb' }
                    }}
                  >
                    Make Payment
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BookingDetails;